/**
 * FS-663: remember where an unauthenticated visitor was heading, so they resume
 * there after the Keycloak round-trip instead of landing on the default board.
 *
 * Why this exists: a share link is useless if clicking it logs you in and then
 * drops you somewhere else. Today the destination is discarded twice — once by
 * `LoginProtectedRoute` (`<Navigate to="/" replace />`, no state) and again by
 * `Login` (a hardcoded `/intelligent-board` redirectUri) — so EVERY recipient of
 * a share link lands on the board library.
 *
 * localStorage, not sessionStorage: a brand-new invitee's Keycloak sign-up
 * includes an email-verification link that is frequently opened in a NEW TAB,
 * which loses sessionStorage. The TTL and the path validation below are what
 * bound the extra exposure that buys.
 *
 * SECURITY: the stored value is handed to Keycloak as a `redirectUri`, so it is
 * an open-redirect sink. `read` accepts only same-origin absolute PATHS — never
 * a scheme, never protocol-relative `//host`. Callers must prefix
 * `window.location.origin` themselves rather than storing an absolute URL.
 */

const STORAGE_KEY = 'intuigence:pending_destination'

/** Long enough for a signup + email verification, short enough that a stale
 *  link from last week cannot hijack an ordinary login. */
const TTL_MS = 30 * 60 * 1000

interface StoredDestination {
  path: string
  savedAt: number
}

/**
 * Same-origin app path only.
 *
 * Rejects: anything not starting with `/`, protocol-relative `//evil.com`
 * (which a browser resolves as a cross-origin URL), backslash variants that
 * some parsers normalise to `//`, and any embedded scheme.
 */
function isSafeAppPath(path: unknown): path is string {
  if (typeof path !== 'string' || path.length === 0) return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//') || path.startsWith('/\\')) return false
  if (/^\/+[\\/]/.test(path)) return false
  // A scheme anywhere before the first slash-delimited segment would let
  // `/redirect?next=https://evil` style values through to a raw assignment.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false
  return true
}

export function setPendingDestination(path: string): void {
  if (!isSafeAppPath(path)) return
  try {
    const payload: StoredDestination = { path, savedAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Private mode / quota — resuming is a nicety, never a hard failure.
  }
}

/** Read without consuming. Returns null when absent, expired or unsafe. */
export function peekPendingDestination(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredDestination>
    if (!isSafeAppPath(parsed.path) || typeof parsed.savedAt !== 'number') {
      clearPendingDestination()
      return null
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      clearPendingDestination()
      return null
    }
    return parsed.path
  } catch {
    return null
  }
}

/** Read and consume, atomically enough for a single tab. */
export function takePendingDestination(): string | null {
  const path = peekPendingDestination()
  if (path !== null) clearPendingDestination()
  return path
}

export function clearPendingDestination(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
